import { useState } from 'react'
import {
  Card, Table, Button, Tag, Space, Typography, Form, Input, Select,
  Switch, Drawer, Row, App,
} from 'antd'
import { PlusOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cottonContractApi, TCTemplate } from '@/features/procurement/cotton_contract_api'

const { Title } = Typography

const CATEGORY_OPTIONS = [
  { value: 'GENERAL', label: 'General' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'QUALITY', label: 'Quality' },
  { value: 'ARBITRATION', label: 'Arbitration' },
]

const CATEGORY_COLORS: Record<string, string> = {
  GENERAL: 'default',
  PAYMENT: 'gold',
  DELIVERY: 'blue',
  QUALITY: 'green',
  ARBITRATION: 'red',
}

export default function TCTemplatesPage() {
  const { message: msg } = App.useApp()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TCTemplate | null>(null)
  const [form] = Form.useForm()

  const { data: templates, isLoading } = useQuery({
    queryKey: ['tc-templates-all'],
    queryFn: () => cottonContractApi.listTemplates(false),
  })

  const createMut = useMutation({
    mutationFn: (data: Partial<TCTemplate>) => cottonContractApi.createTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tc-templates-all'] })
      qc.invalidateQueries({ queryKey: ['tc-templates'] })
      msg.success('Template created')
      setOpen(false)
      form.resetFields()
    },
    onError: (e: any) => msg.error(e?.response?.data?.detail || 'Failed to create template'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TCTemplate> }) =>
      cottonContractApi.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tc-templates-all'] })
      qc.invalidateQueries({ queryKey: ['tc-templates'] })
      msg.success('Template updated')
      setOpen(false)
      setEditing(null)
      form.resetFields()
    },
    onError: (e: any) => msg.error(e?.response?.data?.detail || 'Failed to update template'),
  })

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      if (editing) {
        updateMut.mutate({ id: editing.id, data: vals })
      } else {
        createMut.mutate(vals)
      }
    })
  }

  const openEdit = (tpl: TCTemplate) => {
    setEditing(tpl)
    form.setFieldsValue(tpl)
    setOpen(true)
  }

  const openNew = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, category: 'GENERAL' })
    setOpen(true)
  }

  const columns = [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (v: string) => <b>{v}</b>,
    },
    {
      title: 'Category', dataIndex: 'category', key: 'category', width: 130,
      render: (v: string) => <Tag color={CATEGORY_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Text Preview', dataIndex: 'text', key: 'text', ellipsis: true,
      render: (v: string) => <span style={{ color: '#595959', fontSize: 12 }}>{v?.slice(0, 120)}{v?.length > 120 ? '…' : ''}</span>,
    },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 80,
      render: (v: boolean, r: TCTemplate) => (
        <Switch
          checked={v}
          size="small"
          onChange={(checked) => updateMut.mutate({ id: r.id, data: { is_active: checked } })}
        />
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 80,
      render: (_: unknown, r: TCTemplate) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FileTextOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        T&C Templates
      </Title>
      <Typography.Text type="secondary">Manage Terms & Conditions templates for cotton contracts</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            New Template
          </Button>
        </Row>
        <Table
          dataSource={Array.isArray(templates) ? templates : []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Drawer
        title={editing ? 'Edit T&C Template' : 'New T&C Template'}
        open={open}
        onClose={() => { setOpen(false); setEditing(null); form.resetFields() }}
        width={640}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setOpen(false); setEditing(null) }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSubmit}
              >
                {editing ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Template Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. Standard Cotton Purchase T&C" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item name="text" label="T&C Text" rules={[{ required: true, message: 'Text is required' }]}>
            <Input.TextArea
              rows={12}
              placeholder="Enter the terms and conditions text..."
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
